import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, DownloadSimple, CaretLeft, CaretRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const stripTags = (h) => (h || "").replace(/<[^>]+>/g, " ");
const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function highlight(html, needle) {
  if (!needle) return html;
  const re = new RegExp(`(${escRe(needle)})`, "gi");
  return (html || "")
    .split(/(<[^>]+>)/)
    .map((part) => (part.startsWith("<") ? part : part.replace(re, "<mark>$1</mark>")))
    .join("");
}

export default function Manual() {
  const [sections, setSections] = useState(null);
  const [idx, setIdx] = useState(0);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/manual/sections").then((r) => setSections(r.data.sections || [])).catch(() => setSections([]));
  }, []);

  const needle = q.trim();
  const nlow = needle.toLowerCase();

  const visible = useMemo(() => {
    if (!sections) return [];
    const withIdx = sections.map((s, i) => ({ ...s, _i: i }));
    if (!nlow) return withIdx;
    return withIdx.filter(
      (s) => s.title.toLowerCase().includes(nlow) || stripTags(s.html).toLowerCase().includes(nlow)
    );
  }, [sections, nlow]);

  // Când căutarea schimbă lista, sari la primul capitol care conține potrivirea
  useEffect(() => {
    if (!needle || !visible.length) return;
    if (!visible.some((s) => s._i === idx)) setIdx(visible[0]._i);
    // eslint-disable-next-line
  }, [nlow]);

  if (sections === null) return <div className="p-8 text-muted-foreground">Se încarcă manualul…</div>;
  if (sections.length === 0) return <div className="p-8 text-muted-foreground">Manualul nu este disponibil momentan.</div>;

  const downloadDocx = () => {
    const token = localStorage.getItem("hgs_token");
    window.location.href = `${API}/manual/download?auth=${token}`;
  };

  const order = needle ? visible.map((s) => s._i) : sections.map((_, i) => i);
  const pos = order.indexOf(idx);
  const cur = sections[idx];
  const curHtml = highlight(cur.html, needle);

  const countMatches = (s) => {
    if (!nlow) return 0;
    const hay = (s.title + " " + stripTags(s.html)).toLowerCase();
    let n = 0, from = 0, at;
    while ((at = hay.indexOf(nlow, from)) !== -1) { n++; from = at + nlow.length; }
    return n;
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="manual-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookOpen size={24} weight="bold" className="text-primary" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Ghid de utilizare</p>
            <h1 className="font-head text-3xl font-extrabold tracking-tighter">Manual HGS-OPEP</h1>
          </div>
        </div>
        <Button variant="outline" onClick={downloadDocx} className="gap-2" data-testid="manual-download-docx">
          <DownloadSimple size={18} weight="bold" /> Descarcă DOCX
        </Button>
      </div>

      {/* Căutare */}
      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Caută un pas, un cuvânt sau un departament… (ex: montaj, reclamație, aviz)"
          className="pl-10 pr-24"
          data-testid="manual-search"
        />
        {needle && (
          <button onClick={() => setQ("")} className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="manual-search-clear">
            <X size={14} /> Șterge
          </button>
        )}
      </div>
      {needle && (
        <p className="mb-3 text-xs text-muted-foreground" data-testid="manual-search-info">
          {visible.length === 0 ? "Niciun rezultat." : `${visible.length} capitol(e) conțin „${needle}".`}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Cuprins */}
        <nav className="lg:col-span-1" data-testid="manual-toc">
          <div className="rounded-xl border border-border bg-card p-3 lg:sticky lg:top-4 lg:max-h-[80vh] lg:overflow-y-auto">
            {visible.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">—</p>}
            {visible.map((s) => {
              const c = countMatches(s);
              return (
                <button
                  key={s.id}
                  onClick={() => setIdx(s._i)}
                  data-testid={`manual-toc-${s._i}`}
                  className={cn(
                    "mb-1 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                    s._i === idx ? "bg-primary/15 font-semibold text-primary" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{s.title}</span>
                  {c > 0 && <span className="shrink-0 rounded-full bg-primary/20 px-1.5 text-[10px] font-bold text-primary">{c}</span>}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Conținut capitol */}
        <div className="lg:col-span-3">
          <article className="rounded-xl border border-border bg-card p-6" data-testid="manual-content">
            <h2 className="mb-4 font-head text-2xl font-extrabold tracking-tight" dangerouslySetInnerHTML={{ __html: highlight(cur.title, needle) }} />
            <div className="manual-prose text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: curHtml }} />
          </article>
          <div className="mt-4 flex items-center justify-between">
            <Button variant="outline" size="sm" disabled={pos <= 0} onClick={() => setIdx(order[pos - 1])} className="gap-1.5" data-testid="manual-prev">
              <CaretLeft size={16} /> Anterior
            </Button>
            <span className="text-xs text-muted-foreground">{pos + 1} / {order.length}</span>
            <Button variant="outline" size="sm" disabled={pos >= order.length - 1} onClick={() => setIdx(order[pos + 1])} className="gap-1.5" data-testid="manual-next">
              Următor <CaretRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
